import PropTypes from "prop-types";
import { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  IndianRupee,
  BedDouble,
  MapPin,
  Ruler,
} from "lucide-react";
import FormBox from "../../modal/FormBox";

const DevelopersPageComponent = ({
  developersData,
  heading,
  developersPerPage = 6,
  color = "#384152",
  textcolor = "#FFFFFF",
}) => {
  const developers = Array.isArray(developersData) ? developersData : [];
  const [currentPage, setCurrentPage] = useState(1);
  const [currentFormData, setCurrentFormData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const totalPages = Math.ceil(developers.length / developersPerPage);

  // Slice developers based on the current page
  const currentDevelopers = developers.slice(
    (currentPage - 1) * developersPerPage,
    currentPage * developersPerPage
  );

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleOpenForm = (formdata) => {
    setCurrentFormData(formdata);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setCurrentFormData(null);
  };

  return (
    <div className="w-full flex justify-center" style={{ backgroundColor: color }}>
      <div className="w-full flex gap-14 flex-col sm:w-full md:w-full lg:w-4/5 xl:w-5/6">
        <div className="flex flex-col items-center justify-center p-3 sm:p-6 lg:p-8">
          <h2 className="text-[24px] font-medium mb-12 text-center mt-2" style={{ color: textcolor }}>
            {heading}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
            {currentDevelopers.map((developer, index) => (
              <div
                key={index}
                className="relative group no-underline w-full max-w-[400px] overflow-hidden rounded-lg bg-[#f4f8ff] border-gray-700 border cursor-pointer"
                onClick={() => {
                  if (developer.url) {
                    window.open(developer.url, "_blank");
                  }
                  else{
                    handleOpenForm(developer.form);
                  }
                }}
              >
                <div className="p-3">
                  <img
                    src={developer.image}
                    alt={developer.name}
                    className="w-full h-[200px] xl:h-[220px] object-cover transition-all duration-300 rounded-md"
                  />
                </div>
                <div className="flex flex-col justify-between px-2">
                  <h2 className="flex items-center justify-center text-black text-[18px] font-medium mt-2">
                    {developer.name}
                  </h2>

                  <div className="flex flex-col gap-0 p-2">
                    <div className="flex flex-wrap items-center justify-between">
                      <div className="flex gap-1 flex-1">
                        <IndianRupee className="text-[#374151] font-extrabold w-[18px] h-[18px] flex-shrink-0" />
                        <p className="text-black text-[12px] flex h-full leading-[14px]">
                          {developer.price}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-1">
                        <BedDouble className="text-[#374151] font-extrabold w-[18px] h-[18px] flex-shrink-0" />
                        <p className="text-black text-[12px] leading-[14px]">
                          {developer.bed}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between px-2 mb-2">
                    <div className="flex flex-wrap items-center justify-between">
                      <div className="flex gap-1 flex-1">
                        <MapPin className="text-[#374151] font-extrabold w-[18px] h-[18px] flex-shrink-0" />
                        <p className="text-black text-[12px] leading-[14px]">
                          {developer.location}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-1">
                        <Ruler className="text-[#374151] font-extrabold w-[18px] h-[18px] flex-shrink-0" />
                        <p className="text-black text-[12px] leading-[14px]">
                          {developer.size}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between px-2 pb-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        handleOpenForm(developer.form);
                      }}
                      className="bg-[#374151] text-white text-[14px] h-[35px] w-[140px] rounded-md"
                    >
                      Enquire Now
                    </button>
                    <button
                     onClick={(event) => {
                      event.stopPropagation();
                      if (developer.url) {
                        window.open(developer.url, "_blank");
                      }
                      else{
                        handleOpenForm(developer.form);
                      }
                    }}
                      className="bg-[#374151] text-white text-[14px] h-[35px] w-[140px] rounded-md"
                    >
                      View More
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Buttons */}
          <div className="mt-8 flex items-center gap-4">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className={`${
                currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300"
              } ${color === "#384152" ? "bg-white text-black" : "bg-gray-700 text-white"} px-2 py-1 rounded-md`}
            >
              <ChevronLeft />
            </button>
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={`${
                currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300"
              } ${color === "#384152" ? "bg-white text-black" : "bg-gray-700 text-white"} px-2 py-1 rounded-md`}
            >
              <ChevronRight />
            </button>
          </div>
        </div>

        {/* Pop-up Form */}
        {showForm && currentFormData && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg p-6 shadow-lg relative w-full max-w-lg">
              <button
                onClick={handleCloseForm}
                className="absolute top-2 right-2 text-black bg-gray-200 rounded-full p-2 hover:bg-gray-300"
              >
                ✕
              </button>
              <FormBox onClose={handleCloseForm} formdata={currentFormData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

DevelopersPageComponent.propTypes = {
  developersData: PropTypes.array.isRequired,
  heading: PropTypes.string,
  developersPerPage: PropTypes.number,
};

DevelopersPageComponent.defaultProps = {
  heading: "Properties",
};

export default DevelopersPageComponent;

